// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSettingDetailQuery } from "@/api/hooks/use-settings";
import { GroupBadge } from "@/components/shared/group-badge";
import { KeyChip } from "@/components/shared/key-chip";
import { Separator } from "@/components/ui/separator";
import { FieldTypeBadge } from "@/features/settings/field-type-badge";
import { SettingValueCell } from "@/features/settings/setting-value-cell";
import { cn } from "@/lib/utils";

interface SettingInfoTabProps {
  settingName: string;
}

export function SettingInfoTab({ settingName }: SettingInfoTabProps) {
  const { t } = useTranslation("back-office");
  const { data: setting } = useSettingDetailQuery(settingName);

  if (!setting) return null;

  return (
    <div className="max-w-2xl space-y-6 p-6">
      <div className="space-y-4">
        <div className="grid grid-cols-[160px_1fr] gap-y-3 text-sm">
          <span className="text-muted-foreground">{t("settings.info.fields.key")}</span>
          <span>
            <KeyChip label={setting.key} />
          </span>

          <span className="text-muted-foreground">{t("settings.info.fields.name")}</span>
          <span>{setting.name}</span>

          <span className="text-muted-foreground">{t("settings.info.fields.description")}</span>
          {setting.description ? (
            <CollapsibleDescription text={setting.description.replace(/\\n/g, "\n")} />
          ) : (
            <span className="text-muted-foreground italic">{t("settings.info.fields.noDescription")}</span>
          )}

          <Separator className="col-span-2" />

          <span className="text-muted-foreground">{t("settings.info.fields.group")}</span>
          <span>
            <GroupBadge name={setting.groupName} />
          </span>

          <span className="text-muted-foreground">{t("settings.info.fields.type")}</span>
          <span>
            <FieldTypeBadge type={setting.fieldType} />
          </span>

          <Separator className="col-span-2" />

          <span className="text-muted-foreground">{t("settings.info.fields.defaultValue")}</span>
          <span>
            <SettingValueCell value={setting.defaultValue} fieldType={setting.fieldType} name={setting.name} expanded />
          </span>

          <Separator className="col-span-2" />

          <span className="text-muted-foreground">{t("settings.info.fields.chainOverrides")}</span>
          <span className="tabular-nums">{setting.chainOverridesCount}</span>

          <span className="text-muted-foreground">{t("settings.info.fields.storeOverrides")}</span>
          <span className="tabular-nums">{setting.storeOverridesCount}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Renders a setting description with its newlines and indentation preserved (`whitespace-pre-wrap`
 * — like <br/> but also keeps runs of spaces, no HTML injection). Clamps to 8 lines with a
 * Show more / Show less toggle, shown only
 * when the text actually overflows that height (measured against the clamped element, re-checked
 * on width changes).
 */
function CollapsibleDescription({ text }: { text: string }) {
  const { t } = useTranslation("back-office");
  const ref = useRef<HTMLSpanElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [clampable, setClampable] = useState(false);

  useEffect(
    function measureDescriptionOverflow() {
      const el = ref.current;
      if (!el) return;
      function update() {
        const node = ref.current;
        // While expanded the clamp is off, so the scrollHeight check is meaningless — keep the
        // last known value so the toggle stays put.
        if (!node || expanded) return;
        setClampable(node.scrollHeight > node.clientHeight + 1);
      }
      update();
      const observer = new ResizeObserver(update);
      observer.observe(el);
      return () => observer.disconnect();
    },
    [expanded],
  );

  return (
    <span className="flex min-w-0 flex-col items-start gap-1">
      {/* pre-wrap (not pre-line) so indentation / runs of spaces are preserved, not collapsed.
          Clamp via max-height (8 lines × leading-5) + overflow, NOT -webkit-line-clamp — the
          latter forces display:-webkit-box, which ignores explicit `\n` breaks. */}
      <span ref={ref} className={cn("block whitespace-pre-wrap leading-5", !expanded && "max-h-40 overflow-hidden")}>
        {text}
      </span>
      {clampable && (
        <button type="button" onClick={() => setExpanded((v) => !v)} className="text-xs text-primary hover:underline">
          {expanded ? t("settings.info.showLess") : t("settings.info.showMore")}
        </button>
      )}
    </span>
  );
}
